<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { categoryIcons, categorySelectedIcons, categoryTitles, FACE_WITH_ROLLING_EYES_EMOJI } from '@@/bits/emoji'
import { __ } from '@@/bits/intl'
import { processedEmojiUrl } from '@@/bits/surface_settings_helper'
import { defineAsyncComponent } from '@@/bits/vue'
import OzBackButton, {
  OzBackButtonColorScheme,
  OzBackButtonSizePreset,
} from '@@/library/v4/components/OzBackButton.vue'
import OzCloseButton, { OzCloseButtonSizePreset } from '@@/library/v4/components/OzCloseButton.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzInput, { OzInputSizePreset } from '@@/library/v4/components/OzInput.vue'
import OzPlainButton from '@@/library/v4/components/OzPlainButton.vue'
import OzTabsPaginated, { type OzTabsPaginatedButtonProps } from '@@/library/v4/components/OzTabsPaginated.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import EmojiList, { DEFAULT_COLUMNS } from '@@/vuecomponents/EmojiList.vue'
import { useEmojiPicker } from '@@/vuecomposables/useEmojiPicker'
import { useTooltipTimer } from '@@/vuecomposables/useTooltipTimer'
import { useVirtualKeyboardHeight } from '@@/vuecomposables/virtual_keyboard_height'
import { autoPlacement, offset, type ComputePositionConfig } from '@floating-ui/vue'
import type { Emoji } from '@padlet/arvo'
import { usePreferredDark } from '@vueuse/core'
import { computed, shallowRef } from 'vue'

const ImageThumbnail = defineAsyncComponent(() => import('@@/library/v4/components/ImageThumbnail.vue'))

const props = withDefaults(
  defineProps<{
    darkMode?: boolean | 'auto'
    variant?: 'phone' | 'tablet' | 'desktop'
    xCloseButton?: boolean
    xBackButton?: boolean
    xRecentsCategory?: boolean
    xCategories?: boolean
    columns?: number
    recentEmojiRows?: number
    draggable?: boolean
    searchPlaceholder?: string
    searchAriaLabel?: string
    singleRowLayoutEmptyResultMessage?: string
    defaultEmptyResultMessage?: string
    /**
     * The size of the emoji icons.
     * @default undefined
     * @description If undefined, the icon size will be determined by the variant.
     */
    iconSize?: number
    /**
     * The URL of the currently selected emoji.
     * @default undefined
     */
    selectedEmojiUrl?: string
    /**
     * Function to determine whether to update recents when an emoji is picked.
     * If not provided, defaults to always adding to recents.
     * @default undefined
     */
    shouldAddToRecents?: (Emoji: Emoji) => boolean
    /**
     * Whether to show a checkmark next to the selected emoji.
     * @default true
     */
    xCheckmark?: boolean
  }>(),
  {
    darkMode: 'auto',
    variant: 'desktop',
    xCloseButton: false,
    xBackButton: false,
    xRecentsCategory: true,
    xCategories: true,
    columns: DEFAULT_COLUMNS,
    recentEmojiRows: 2,
    draggable: false,
    searchPlaceholder: __('Search emoji'),
    searchAriaLabel: __('Search emoji'),
    singleRowLayoutEmptyResultMessage: __('Sticker box empty – search again?'),
    defaultEmptyResultMessage: __('We couldn’t find any stickers matching your search.'),
    iconSize: undefined,
    selectedEmojiUrl: undefined,
    shouldAddToRecents: undefined,
    xCheckmark: true,
  },
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'back'): void
  (e: 'pick', emojiUrl: string)
}>()

const { isVirtualKeyboardShowing } = useVirtualKeyboardHeight()

const emojiListLayout = computed(() => {
  // Switch to single-row layout when virtual keyboard is shown on tablet variant
  if (props.variant === 'tablet' && isVirtualKeyboardShowing.value) {
    return 'single-row'
  }
  return 'grid'
})

const actualDarkMode = computed<boolean>(() => {
  if (props.darkMode === 'auto') {
    return usePreferredDark().value
  }
  return props.darkMode === true
})

const safeColumns = computed(() => Math.max(1, Math.floor(props.columns || DEFAULT_COLUMNS)))

const maxRecentEmojis = computed(() => props.recentEmojiRows * safeColumns.value)

const {
  isLoading,
  displayedCategories,
  switchCategory,
  setCurrentCategoryIndex,
  emojiSearchQuery,
  isSearchInputFocused,
  isInSearchMode,
  currentCategoryIndex,
  ariaLiveMessage,
  xNoEmojiFound,
  currentShownEmojis,
  debouncedSearch,
  resetSearch,
  handleEmojiPick,
} = useEmojiPicker({
  recentsCategoryEnabled: props.xRecentsCategory,
  maxRecentEmojis,
  shouldShowAllEmojis: true,
  trackingContext: 'EmojiPicker',
  onPick: (emojiUrl: string) => emit('pick', emojiUrl),
  shouldAddToRecents: props.shouldAddToRecents,
})

const tabs = computed<OzTabsPaginatedButtonProps[]>(() => {
  return displayedCategories.value.map((category, index) => ({
    key: category,
    ariaLabel: categoryTitles[category],
    text: categoryTitles[category],
    iconName: currentCategoryIndex.value === index ? categorySelectedIcons[category] : categoryIcons[category],
    iconSize: 24,
    testId: `emoji-tab-${category}`,
  }))
})

const emojiListRef = shallowRef<InstanceType<typeof EmojiList> | null>(null)

const xHeaderDivider = computed(() => {
  // Only show divider for grid layout
  return emojiListLayout.value === 'grid' && (emojiListRef.value?.isScrolled ?? false)
})

const handleTabActivation = (_event: Event, payload: { key: string }) => {
  switchCategory(payload.key)
  trackEvent('Emoji Picker', 'Switched category', payload.key, undefined, {
    trackingContext: 'EmojiPicker',
  })

  // Scroll to the category in the EmojiList if not in search mode
  if (!isInSearchMode.value && emojiListRef.value) {
    emojiListRef.value.scrollToCategory(payload.key)
  }
}

const handleCategoryVisible = (categoryLabel: string) => {
  // Only update the tab if we're not in search mode
  if (!isInSearchMode.value) {
    const index = displayedCategories.value.findIndex((c) => c === categoryLabel)
    if (index >= 0 && index !== currentCategoryIndex.value) {
      setCurrentCategoryIndex(index)
    }
  }
}

const effectiveMaxItemsPerPage = computed(() => {
  // For desktop variant, we show the same number of tabs as the number of emoji columns
  if (props.variant === 'desktop') {
    return safeColumns.value
  }

  // For other variants, use the actual number of displayed categories
  // displayedCategories already handles the logic for including/excluding recents
  return displayedCategories.value.length
})

// #region Unified tooltip

const tooltipRef = shallowRef<InstanceType<typeof OzTooltip> | null>(null)
// The hovered element can be either a tab button or an emoji element
const hoveredElement = shallowRef<HTMLElement | null>(null)
// Track what type of tooltip we're showing
type TooltipContent = { type: 'category'; content: string } | { type: 'emoji'; content: string }
const tooltipContent = shallowRef<TooltipContent | null>(null)

// Tooltip config for category tabs (positioned above)
const CATEGORY_TOOLTIP_CONFIG: Partial<ComputePositionConfig> = {
  strategy: 'fixed',
  middleware: [
    offset({
      mainAxis: 4,
    }),
    autoPlacement({
      allowedPlacements: ['top', 'top-start', 'top-end'],
    }),
  ],
}

// Tooltip config for emojis (positioned below)
const EMOJI_TOOLTIP_CONFIG: Partial<ComputePositionConfig> = {
  strategy: 'fixed',
  middleware: [
    offset({
      mainAxis: 4,
    }),
    autoPlacement({
      allowedPlacements: ['bottom', 'bottom-start', 'bottom-end'],
    }),
  ],
}

// Dynamic tooltip config based on content type
const tooltipConfig = computed(() => {
  return tooltipContent.value?.type === 'emoji' ? EMOJI_TOOLTIP_CONFIG : CATEGORY_TOOLTIP_CONFIG
})

// Track what the timer is showing (category or emoji)
let pendingTooltip: { target: HTMLElement; key: string; content: TooltipContent } | null = null

const tooltipTimer = useTooltipTimer({
  onShow: (key) => {
    // Only show tooltip if the key matches the pending tooltip
    if (pendingTooltip && pendingTooltip.key === key) {
      hoveredElement.value = pendingTooltip.target
      tooltipContent.value = pendingTooltip.content
    }
  },
  onHide: () => {
    hoveredElement.value = null
    tooltipContent.value = null
  },
})

const setHoveredEmojiCategory = (event: MouseEvent | FocusEvent, tab: OzTabsPaginatedButtonProps) => {
  // Skip all tooltip logic on touch devices
  if (device.touchable) return

  const target = (event.currentTarget ?? event.target) as HTMLElement

  if (event instanceof FocusEvent) {
    // Focus shows immediately, bypass the timer
    hoveredElement.value = target
    tooltipContent.value = { type: 'category', content: tab.ariaLabel }
  } else {
    // Mouse hover uses the timer
    pendingTooltip = {
      target,
      key: tab.key,
      content: { type: 'category', content: tab.ariaLabel },
    }
    tooltipTimer.requestShow(tab.key)
  }
}

const setHoveredEmoji = (event: MouseEvent | FocusEvent, emoji: Emoji) => {
  // Skip all tooltip logic on touch devices
  if (device.touchable) return

  const target = (event.currentTarget ?? event.target) as HTMLElement

  if (event instanceof FocusEvent) {
    // Focus shows immediately, bypass the timer
    hoveredElement.value = target
    tooltipContent.value = { type: 'emoji', content: emoji.name }
  } else {
    // Mouse hover uses the timer
    pendingTooltip = {
      target,
      key: emoji.code,
      content: { type: 'emoji', content: emoji.name },
    }
    tooltipTimer.requestShow(emoji.code)
  }
}

const unsetHoveredTooltip = () => {
  // Skip all tooltip logic on touch devices
  if (device.touchable) return

  tooltipTimer.requestHide()
  pendingTooltip = null
  hoveredElement.value = null
  tooltipContent.value = null
}
// #endregion

// #region icon size
const emojiIconSize = computed(() => {
  if (props.iconSize) {
    return props.iconSize
  }
  return props.variant === 'desktop' ? 32 : 40
})
const hoveredEmojiIconSize = computed(() => {
  return emojiIconSize.value + (props.variant === 'desktop' ? 4 : 0)
})
// #endregion

// #region Exposing functionality
const searchInputRef = shallowRef<InstanceType<typeof OzInput> | null>(null)

const focusSearchBar = () => {
  searchInputRef.value?.focusInput()
}

defineExpose({
  focusSearchBar,
})
// #endregion
</script>

<template>
  <div class="relative flex flex-col" data-testid="emojiPicker">
    <div
      :class="[
        'relative',
        'flex',
        'justify-between',
        'items-center',
        { 'gap-4': variant === 'phone', 'gap-2': variant !== 'phone' },
        'p-4',
      ]"
    >
      <OzBackButton
        v-if="xBackButton"
        :dark-mode="actualDarkMode"
        :size-preset="variant === 'phone' ? OzBackButtonSizePreset.H40px : OzBackButtonSizePreset.H32px"
        :color-scheme="variant === 'phone' ? OzBackButtonColorScheme.Secondary : OzBackButtonColorScheme.SecondaryClear"
        :class="[
          'shrink-0',
          isSearchInputFocused && {
            'hhover:ring-2 hhover:ring-offset-0': true,
            'hhover:ring-overlay-text-bg-light-mode-light-hover': !actualDarkMode,
            'hhover:ring-overlay-text-bg-dark-mode-dark-hover': actualDarkMode,
          },
        ]"
        @click="$emit('back')"
      />
      <OzCloseButton
        v-else-if="xCloseButton"
        :dark-mode="actualDarkMode"
        :size-preset="variant === 'phone' ? OzCloseButtonSizePreset.H40px : OzCloseButtonSizePreset.H32px"
        :class="[
          'shrink-0',
          isSearchInputFocused && {
            'hhover:ring-2 hhover:ring-offset-0': true,
            'hhover:ring-overlay-text-bg-light-mode-light-hover': !actualDarkMode,
            'hhover:ring-overlay-text-bg-dark-mode-dark-hover': actualDarkMode,
          },
        ]"
        @click="$emit('close')"
      />
      <OzInput
        id="emoji-search"
        ref="searchInputRef"
        test-id="emoji-search-input"
        dir="auto"
        type="search"
        name="emoji-search"
        enterkeyhint="search"
        :model-value="emojiSearchQuery"
        :placeholder="searchPlaceholder"
        :aria-label="searchAriaLabel"
        aria-controls="emoji-list"
        :x-border="false"
        :class="[
          'grow',
          'py-2 px-3',
          '!border-2',
          isSearchInputFocused && [
            'ring-2',
            {
              'ring-grape-500': !actualDarkMode,
              'ring-canary-500': actualDarkMode,
            },
          ],
          {
            'bg-attachment-overlay-light': !actualDarkMode,
            'bg-white-400': actualDarkMode,
          },
          'backdrop-blur-[2px]',
        ]"
        :size-preset="variant === 'phone' ? OzInputSizePreset.H40px : OzInputSizePreset.H32px"
        :dark-mode="actualDarkMode"
        @input="debouncedSearch"
        @focusin="isSearchInputFocused = true"
        @blur="isSearchInputFocused = false"
      >
        <template #startAdornment>
          <OzIcon
            name="search"
            :size="20"
            :class="[
              'ms-3 me-2',
              {
                'text-dark-text-200': !actualDarkMode,
                'text-light-text-200': actualDarkMode,
              },
            ]"
          />
        </template>
        <template v-if="isInSearchMode" #endAdornment>
          <OzPlainButton
            type="button"
            :aria-label="__('Reset search')"
            :title="__('Reset search')"
            :class="[
              'group',
              'rounded-lg',
              'focus-visible:outline-none focus-visible:ring-2',
              {
                'focus-visible:ring-grape-500': !actualDarkMode,
                'focus-visible:ring-canary-500': actualDarkMode,
              },
              'ms-1.5 me-2.5',
              'ps-0.5 pe-0.5',
            ]"
            @click="resetSearch"
          >
            <OzIcon
              :class="[
                'block',
                {
                  'text-dark-text-300': !actualDarkMode,
                  'text-light-text-300': actualDarkMode,
                  'hover-hover:group-hover:text-dark-text-100': !actualDarkMode,
                  'hover-hover:group-hover:text-light-text-100': actualDarkMode,
                },
              ]"
              :size="20"
              :name="'clear_filled'"
            />
          </OzPlainButton>
        </template>
      </OzInput>
      <OzDivider
        v-show="xHeaderDivider"
        class="absolute inset-x-0 bottom-0 mx-4"
        :color-scheme="OzDividerColorScheme.Solid"
        :dark-mode="actualDarkMode"
      />
    </div>

    <!-- Don't move the v-if to this div. We need to keep `aria-live` -->
    <!-- in the DOM regardless of search results. -->
    <div aria-live="polite" :class="[xNoEmojiFound && 'h-full px-8 pb-3']">
      <div
        v-if="xNoEmojiFound"
        :class="[
          'flex flex-col',
          'items-center justify-center',
          'gap-4',
          'h-full',
          'text-center',
          {
            'text-dark-text-100': !actualDarkMode,
            'text-light-text-100': actualDarkMode,
          },
        ]"
        data-testid="emoji-picker-no-results"
      >
        <ImageThumbnail
          v-if="emojiListLayout !== 'single-row'"
          :original-image-width="48"
          :original-image-height="48"
          :width="48"
          :src="processedEmojiUrl(FACE_WITH_ROLLING_EYES_EMOJI.iconSrc)"
          :title="FACE_WITH_ROLLING_EYES_EMOJI.name"
        />
        <p>
          {{ emojiListLayout === 'single-row' ? singleRowLayoutEmptyResultMessage : defaultEmptyResultMessage }}
        </p>
      </div>
      <p v-else-if="ariaLiveMessage" class="sr-only">{{ ariaLiveMessage }}</p>
    </div>

    <EmojiList
      v-show="!xNoEmojiFound"
      id="emoji-list"
      ref="emojiListRef"
      :class="[!xNoEmojiFound && 'h-full', 'px-2']"
      :is-loading="isLoading"
      :emojis="currentShownEmojis"
      :dark-mode="actualDarkMode"
      :layout="emojiListLayout"
      :columns="safeColumns"
      :icon-size="emojiIconSize"
      :hovered-icon-size="hoveredEmojiIconSize"
      :group-by-category="true"
      :x-thin-scrollbar="true"
      :selected-emoji-url="selectedEmojiUrl"
      :draggable="draggable"
      :x-checkmark="xCheckmark"
      @pick="handleEmojiPick"
      @category-visible="handleCategoryVisible"
      @emoji-hover="setHoveredEmoji"
      @emoji-unhover="unsetHoveredTooltip"
    />

    <!-- We have 9 emoji categories in total. We show them all in phone. -->
    <!-- In desktop, we show the same number of columns in the tab bar -->
    <!-- as the number of emoji columns. -->
    <OzTabsPaginated
      v-if="xCategories"
      :tab-list-aria-label="__('Emoji categories')"
      aria-controls="emoji-list"
      :current-tab-key="tabs[currentCategoryIndex] ? tabs[currentCategoryIndex].key : undefined"
      :tabs="tabs"
      :max-items-per-page="effectiveMaxItemsPerPage"
      :dark-mode="actualDarkMode"
      :class="[
        'sticky',
        'bottom-0',
        {
          'px-3': variant === 'phone',
          'px-4': variant === 'tablet',
          'px-2': variant === 'desktop',
        },
        'py-1',
        'border-t-0.5',
        'border-solid',
        {
          'border-divider-gray': !actualDarkMode,
          'border-divider-gray-dark': actualDarkMode,
        },
      ]"
      @activate-tab="handleTabActivation"
      @mouseenter="setHoveredEmojiCategory"
      @mouseleave="unsetHoveredTooltip"
      @focus="setHoveredEmojiCategory"
      @blur="unsetHoveredTooltip"
    />

    <Transition name="fade">
      <OzTooltip
        v-if="hoveredElement && tooltipContent && !device.touchable"
        ref="tooltipRef"
        :anchor="hoveredElement"
        :config="tooltipConfig"
        :class="['z-whiteboard-toolbar-tooltip', 'px-3 py-1', 'text-body-small', 'whitespace-nowrap']"
        :dark-mode="!actualDarkMode"
      >
        {{ tooltipContent.content }}
      </OzTooltip>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
